import PropTypes from 'prop-types';
import { useCallback, useState, useEffect } from 'react';
// @dnd-kit
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
// @mui
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
// components
import Iconify from 'src/components/iconify';
import { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

// Sortable Column Item Component
function SortableColumnItem({ column, checked, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 0.5,
        borderRadius: 1,
        bgcolor: isDragging ? 'action.hover' : 'transparent',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          px: 1,
          py: 0.5,
          color: 'text.disabled',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <Iconify icon="nimbus:drag-dots" width={16} />
      </Box>

      {/* Checkbox and Label */}
      <FormControlLabel
        control={
          <Checkbox checked={checked} onChange={(e) => onToggle(column.id, e.target.checked)} />
        }
        label={<Typography variant="body2">{column.label}</Typography>}
        sx={{ flex: 1, m: 0 }}
      />
    </Box>
  );
}

SortableColumnItem.propTypes = {
  column: PropTypes.object.isRequired,
  checked: PropTypes.bool,
  onToggle: PropTypes.func,
};

// ----------------------------------------------------------------------

export default function TableColumnVisibility({
  columns,
  visibleColumns,
  onToggleColumn,
  onReorder,
  onReset,
}) {
  const popover = usePopover();

  // ✅ Pending state - changes only apply when "Apply" is clicked
  const [pendingVisibleColumns, setPendingVisibleColumns] = useState(visibleColumns);
  const [pendingItems, setPendingItems] = useState(columns.map((col) => col.id));

  // ✅ Sync pending state when popover opens
  useEffect(() => {
    if (popover.open) {
      setPendingVisibleColumns(visibleColumns);
      setPendingItems(columns.map((col) => col.id));
    }
  }, [popover.open, visibleColumns, columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const allVisible = columns.every((col) => pendingVisibleColumns[col.id]);
  const someVisible = columns.some((col) => pendingVisibleColumns[col.id]) && !allVisible;

  // ✅ Toggle all - only updates pending state
  const handleToggleAll = useCallback(() => {
    const newState = !allVisible;
    const newVisibility = {};
    columns.forEach((col) => {
      newVisibility[col.id] = newState;
    });
    setPendingVisibleColumns(newVisibility);
  }, [allVisible, columns]);

  // ✅ Toggle individual column - only updates pending state
  const handleToggleColumn = useCallback((columnId, checked) => {
    setPendingVisibleColumns((prev) => ({
      ...prev,
      [columnId]: checked,
    }));
  }, []);

  // ✅ Drag end - only updates pending state
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPendingItems((prevItems) => {
        const oldIndex = prevItems.indexOf(active.id);
        const newIndex = prevItems.indexOf(over.id);
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  }, []);

  // ✅ Reset to default
  const handleReset = useCallback(() => {
    const defaultVisibility = {};
    columns.forEach((col) => {
      defaultVisibility[col.id] = true;
    });
    setPendingVisibleColumns(defaultVisibility);
    setPendingItems(columns.map((col) => col.id));
  }, [columns]);

  // ✅ Apply changes - Update parent component and close popover
  const handleApply = useCallback(() => {
    // Apply visibility changes
    Object.keys(pendingVisibleColumns).forEach((columnId) => {
      if (pendingVisibleColumns[columnId] !== visibleColumns[columnId]) {
        onToggleColumn(columnId, pendingVisibleColumns[columnId]);
      }
    });

    // Apply column reordering
    const reorderedColumns = pendingItems
      .map((id) => columns.find((col) => col.id === id))
      .filter(Boolean);

    if (onReorder) {
      onReorder(reorderedColumns);
    }

    // Close popover
    popover.onClose();
  }, [
    pendingVisibleColumns,
    pendingItems,
    visibleColumns,
    columns,
    onToggleColumn,
    onReorder,
    popover,
  ]);

  // ✅ Reset and apply immediately
  const handleResetAndApply = useCallback(() => {
    onReset();
    popover.onClose();
  }, [onReset, popover]);

  // ✅ Cancel - close without applying
  const handleCancel = useCallback(() => {
    popover.onClose();
  }, [popover]);

  const visibleCount = Object.values(pendingVisibleColumns).filter(Boolean).length;

  // Get ordered columns based on pending items state
  const orderedColumns = pendingItems
    .map((id) => columns.find((col) => col.id === id))
    .filter(Boolean);

  return (
    <>
      <IconButton onClick={popover.onOpen}>
        <Iconify icon="solar:settings-bold-duotone" />
      </IconButton>

      <Popover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={handleCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 300,
            p: 0,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle2">Show Columns</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {visibleCount} of {columns.length}
          </Typography>
        </Box>

        <Divider />

        {/* Body */}
        <Box sx={{ px: 1.5, py: 1, maxHeight: 400, overflow: 'auto' }}>
          {/* Select All */}
          <FormControlLabel
            control={
              <Checkbox
                checked={allVisible}
                indeterminate={someVisible}
                onChange={handleToggleAll}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Select All
              </Typography>
            }
            sx={{ mb: 0.5 }}
          />

          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

          {/* Drag & Drop Hint */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              mb: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'info.lighter',
            }}
          >
            <Iconify icon="eva:info-outline" width={16} sx={{ color: 'info.main' }} />
            <Typography variant="caption" sx={{ color: 'info.darker' }}>
              Drag to reorder columns
            </Typography>
          </Stack>

          {/* Sortable Column List */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={pendingItems} strategy={verticalListSortingStrategy}>
              {orderedColumns.map((column) => (
                <SortableColumnItem
                  key={column.id}
                  column={column}
                  checked={pendingVisibleColumns[column.id]}
                  onToggle={handleToggleColumn}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>

        <Divider />

        {/* Footer Actions */}
        <Stack direction="row" spacing={1} sx={{ px: 1.5, py: 1 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color="inherit"
            onClick={handleResetAndApply}
          >
            Reset
          </Button>
          <Button fullWidth size="small" variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button fullWidth size="small" variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Stack>
      </Popover>
    </>
  );
}

TableColumnVisibility.propTypes = {
  columns: PropTypes.array.isRequired,
  visibleColumns: PropTypes.object.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  onReset: PropTypes.func.isRequired,
};
